import { usePageRefresh } from '../../../../context/PageRefreshContext';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getIncomeExpenditureReport } from '../../../../redux/actions/SocietyPortal/AccountReportsAction/AccountReportsAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import ReportShell, { ReportActionButtons } from '../components/ReportShell';
import { FinancialYearField, DateField, GetReportButton } from '../components/ReportFilters';
import TwoColumnReport from '../components/TwoColumnReport';
import { getCurrentFY, fyDateRange, fyShortLabel, formatCurrency } from '../components/helpers';

const IncomeExpenditure = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();
    const { ieData, ieLoading } = useSelector(state => state.AccountReportsReducer || {});

    const [financialYear, setFinancialYear] = useState(getCurrentFY());
    const [fromDate, setFromDate] = useState(fyDateRange(getCurrentFY()).from);
    const [toDate, setToDate] = useState(fyDateRange(getCurrentFY()).to);
    const [collapseSignal, setCollapseSignal] = useState(0);

    useEffect(() => {
        const { from, to } = fyDateRange(financialYear);
        setFromDate(from);
        setToDate(to);
    }, [financialYear]);

    const handleGet = () => {
        if (!societyId) return;
        dispatch(getIncomeExpenditureReport({ societyId, fromDate, toDate }));
    };
    usePageRefresh(handleGet);

    useEffect(() => { handleGet(); /* eslint-disable-next-line */ }, [societyId]);

    const [a, b] = financialYear.split('-');
    const prevFY = a && b ? `${parseInt(a) - 1}-${parseInt(b) - 1}` : '';
    const columns = [
        { label: `Current Year (${fyShortLabel(financialYear)})` },
        { label: `Previous Year (${fyShortLabel(prevFY)})`, muted: true },
    ];

    // Income/Expenditure are single root objects with details[] children.
    // Wrap each in an array so the renderer treats it as a top-level group.
    const incomeNodes = ieData?.income ? [ieData.income] : [];
    const expenditureNodes = ieData?.expenditure ? [ieData.expenditure] : [];

    const totals = ieData?.totals || {};
    const totalIncome = parseFloat(totals.currentIncome) || 0;
    const totalExpenditure = parseFloat(totals.currentExpenditure) || 0;
    const prevTotalIncome = parseFloat(totals.previousIncome) || 0;
    const prevTotalExpenditure = parseFloat(totals.previousExpenditure) || 0;

    // Profit/Loss balancing entry. In a two-column I&E:
    //   Profit: surplus shown on Expenditure side to balance
    //   Loss:   deficit shown on Income side to balance
    const pl = ieData?.profitLoss || {};
    const cy = pl.currentYear || {};
    const py = pl.previousYear || {};
    const cyAmount = parseFloat(cy.amount) || 0;
    const pyAmount = parseFloat(py.amount) || 0;
    const cyIsProfit = cy.type === 'Profit';
    const pyIsProfit = py.type === 'Profit';

    const expenditureExtra = cyAmount > 0 || pyAmount > 0
        ? {
            label: cyIsProfit ? 'Surplus (Profit)' : 'Deficit (Loss)',
            current_year_amount: cyIsProfit ? cyAmount : 0,
            previous_year_amount: pyIsProfit ? pyAmount : 0,
        }
        : null;

    const incomeExtra = cyAmount > 0 || pyAmount > 0
        ? {
            label: !cyIsProfit ? 'Deficit (Loss)' : 'Surplus (Profit)',
            current_year_amount: !cyIsProfit ? cyAmount : 0,
            previous_year_amount: !pyIsProfit ? pyAmount : 0,
        }
        : null;

    // Grand totals (each side balances after adding the P&L row)
    const grandLeft = totalExpenditure + (cyIsProfit ? cyAmount : 0);
    const grandRight = totalIncome + (!cyIsProfit ? cyAmount : 0);
    const grandLeftPrev = prevTotalExpenditure + (pyIsProfit ? pyAmount : 0);
    const grandRightPrev = prevTotalIncome + (!pyIsProfit ? pyAmount : 0);

    return (
        <ReportShell
            title="Income & Expenditure Report"
            actions={<ReportActionButtons
                onCollapseAll={() => setCollapseSignal((s) => s + 1)}
                onExcel={() => window.print()}
                onPrint={() => window.print()}
            />}
            filters={(
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3 tw-items-end">
                    <FinancialYearField value={financialYear} onChange={setFinancialYear} />
                    <DateField label="From Date" value={fromDate} onChange={setFromDate} />
                    <DateField label="To Date" value={toDate} onChange={setToDate} />
                    <GetReportButton onClick={handleGet} loading={ieLoading} />
                </div>
            )}
        >
            {ieLoading && !ieData ? (
                <div className="tw-flex tw-items-center tw-justify-center tw-py-16">
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                        <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-rose-500"></div>
                        <span className="tw-text-xs tw-text-gray-400">Loading report...</span>
                    </div>
                </div>
            ) : (
                <>
                    <TwoColumnReport
                        columns={columns}
                        leftTitle="Expenditure"
                        leftNodes={expenditureNodes}
                        leftTotalLabel="Total Expenditure"
                        leftTotalCurrent={totalExpenditure}
                        leftTotalPrevious={prevTotalExpenditure}
                        leftExtraRow={expenditureExtra}
                        rightTitle="Income"
                        rightNodes={incomeNodes}
                        rightTotalLabel="Total Income"
                        rightTotalCurrent={totalIncome}
                        rightTotalPrevious={prevTotalIncome}
                        rightExtraRow={incomeExtra}
                        collapseAllSignal={collapseSignal}
                    />

                    {ieData && (
                        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-bg-gray-100 tw-border-t-2 tw-border-gray-300 tw-text-xs tw-font-bold">
                            <div className="tw-px-4 tw-py-3 tw-flex tw-items-center tw-justify-between tw-border-b lg:tw-border-b-0 lg:tw-border-r tw-border-gray-200">
                                <span className="tw-text-gray-800">Grand Total</span>
                                <div className="tw-flex tw-gap-6">
                                    <span className="tw-text-gray-900">{formatCurrency(grandLeft)}</span>
                                    <span className="tw-text-gray-600">{formatCurrency(grandLeftPrev)}</span>
                                </div>
                            </div>
                            <div className="tw-px-4 tw-py-3 tw-flex tw-items-center tw-justify-between">
                                <span className="tw-text-gray-800">Grand Total</span>
                                <div className="tw-flex tw-gap-6">
                                    <span className="tw-text-gray-900">{formatCurrency(grandRight)}</span>
                                    <span className="tw-text-gray-600">{formatCurrency(grandRightPrev)}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </ReportShell>
    );
};

export default IncomeExpenditure;
